/**
 * What must be true — first-class assumptions from the Launch Calendar run.
 * Slice 1 list + Slice 2 lazy shadow impact on expand.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleDashed,
  Lightbulb,
  Loader2,
} from 'lucide-react';
import type {
  TimePhasedAssumption,
  TimePhasedAssumptionStatus,
  TimePhasedAssumptionsSnapshot,
} from '../../../lib/timePhasedAssumptions';
import { impactMagnitude } from '../../../lib/timePhasedAssumptions';
import {
  computeAssumptionShadowImpact,
  getCachedShadowImpact,
  setCachedShadowImpact,
} from '../../../lib/assumptionShadowImpact';
import type {
  TimePhasedEngineInput,
  TimePhasedEngineOutput,
} from '../../../lib/timePhasedPlanning.types';
import { getItemCountState } from '../../collection/types';

const KIND_LABEL: Record<TimePhasedAssumption['kind'], string> = {
  'resource-allocation': 'Resource',
  'date-hold': 'Date hold',
  'dependency-delivery': 'Dependency',
  capacity: 'Capacity',
  funding: 'Funding',
};

const STATUS_TONE: Record<
  TimePhasedAssumptionStatus,
  { chip: string; icon: typeof CheckCircle2; label: string }
> = {
  unvalidated: {
    chip: 'bg-[var(--hover-tint)] text-[var(--text-sec)]',
    icon: CircleDashed,
    label: 'unvalidated',
  },
  confirmed: {
    chip: 'bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/40 dark:text-[var(--text-ter)]',
    icon: CheckCircle2,
    label: 'confirmed',
  },
  broken: {
    chip: 'bg-[var(--coral-dim)] text-[var(--coral)] dark:bg-[var(--coral)]/40 dark:text-[var(--coral)]',
    icon: AlertTriangle,
    label: 'broken',
  },
};

function ImpactBlock({
  a,
  shadowLoading,
}: {
  a: TimePhasedAssumption;
  shadowLoading?: boolean;
}) {
  const impact = a.impact;
  if (shadowLoading) {
    return (
      <p className="mt-2 text-sm text-[var(--text-ter)] inline-flex items-center gap-2">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Computing shadow impact…
      </p>
    );
  }
  if (!impact) {
    return a.impactIfFalse ? (
      <p className="mt-1.5 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
        <span className="font-medium">If false: </span>
        {a.impactIfFalse}
      </p>
    ) : null;
  }

  const { launchesAffected, teamsOverload, fundingBreaks, summary } = impact;
  const hasAny =
    launchesAffected.length > 0 || teamsOverload.length > 0 || fundingBreaks.length > 0;

  return (
    <div className="mt-2 space-y-2 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
      {(summary || a.impactIfFalse) && (
        <p>
          <span className="font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
            If this breaks:{' '}
          </span>
          {(summary || a.impactIfFalse || '').replace(/^If this breaks:\s*/i, '')}
        </p>
      )}
      {hasAny && (
        <div className="bg-[var(--surface)] dark:bg-[var(--bg)] border border-[var(--rule)] dark:border-[var(--rule)] p-2.5 space-y-1.5">
          {launchesAffected.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-1">
                Launches affected
              </p>
              <ul className="space-y-0.5">
                {launchesAffected.slice(0, 8).map((l) => (
                  <li key={l.priorityId}>
                    {l.priorityName}
                    {l.oldDate && l.newDate && l.oldDate !== l.newDate ? (
                      <span className="font-mono text-base text-[var(--text-ter)]">
                        {' '}
                        {l.oldDate} → {l.newDate}
                      </span>
                    ) : null}
                    <span className="text-[var(--text-ter)]"> — {l.effect}</span>
                    {l.slipWeeks != null && l.slipWeeks > 0 ? ` (+${l.slipWeeks}w)` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {teamsOverload.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-1">
                Teams overload
              </p>
              <ul className="space-y-0.5">
                {teamsOverload.slice(0, 8).map((t) => (
                  <li key={`${t.resourceType}-${t.bucketStart}`}>
                    {t.resourceType} week of {t.bucketStart}
                    {t.utilizationPct > 0 ? ` · ${Math.round(t.utilizationPct)}%` : ''}
                    {t.overageHours != null && t.overageHours > 0
                      ? ` · +${Math.round(t.overageHours)}h`
                      : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {fundingBreaks.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-1">
                $ periods break
              </p>
              <ul className="space-y-0.5">
                {fundingBreaks.slice(0, 8).map((f) => (
                  <li key={f.bucketStart}>
                    {f.bucketStart}: ${Math.round(f.overage).toLocaleString()} overage
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AssumptionRow({
  a,
  runKey,
  baselineInput,
  baselineOutput,
  onStatusChange,
}: {
  a: TimePhasedAssumption;
  runKey?: string;
  baselineInput?: TimePhasedEngineInput | null;
  baselineOutput?: TimePhasedEngineOutput | null;
  onStatusChange?: (id: string, status: TimePhasedAssumptionStatus) => void;
}) {
  const [open, setOpen] = useState(a.status === 'broken');
  const [row, setRow] = useState(a);
  const [shadowLoading, setShadowLoading] = useState(false);
  const tone = STATUS_TONE[row.status];
  const Icon = tone.icon;

  useEffect(() => {
    setRow(a);
  }, [a]);

  useEffect(() => {
    if (!open || !baselineInput || !baselineOutput || !runKey) return;
    const assumptionId = a.id;
    const cached = getCachedShadowImpact(runKey, assumptionId);
    if (cached) {
      setRow((prev) => ({
        ...prev,
        impact: cached.impact,
        impactIfFalse: cached.impact.summary ?? prev.impactIfFalse,
      }));
      return;
    }
    let cancelled = false;
    setShadowLoading(true);
    queueMicrotask(() => {
      try {
        const result = computeAssumptionShadowImpact({
          assumption: a,
          baselineInput,
          baselineOutput,
        });
        setCachedShadowImpact(runKey, result);
        if (!cancelled) {
          setRow((prev) => ({
            ...prev,
            impact: result.impact,
            impactIfFalse: result.impact.summary ?? prev.impactIfFalse,
          }));
        }
      } finally {
        if (!cancelled) setShadowLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [open, runKey, baselineInput, baselineOutput, a]);

  return (
    <li className="border-b border-[var(--rule)] dark:border-[var(--rule)] last:border-0">
      <div className="px-3 py-2.5 flex items-start gap-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="mt-0.5 text-[var(--text-ter)] hover:text-[var(--text-sec)]"
          aria-expanded={open}
          aria-label={open ? 'Collapse' : 'Expand impact'}
        >
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        <Icon className="h-4 w-4 shrink-0 mt-0.5 text-[var(--text-ter)]" />
        <div className="min-w-0 flex-1">
          <button type="button" onClick={() => setOpen((v) => !v)} className="w-full text-left">
            <div className="flex flex-wrap items-center gap-2 mb-0.5">
              <span className="text-xs font-medium uppercase tracking-wide text-[var(--text-ter)]">
                {KIND_LABEL[row.kind]}
              </span>
              <span className={`text-xs px-1.5 py-0.5 rounded ${tone.chip}`}>{tone.label}</span>
              {row.quantity != null && (
                <span className="text-xs font-mono text-base text-[var(--text-ter)]">
                  {row.quantity}
                  {row.unit === 'hours' ? 'h' : row.unit === 'dollars' ? '$' : ''}
                </span>
              )}
              {row.subject && (
                <span className="text-xs text-[var(--text-ter)] truncate max-w-[140px]">{row.subject}</span>
              )}
            </div>
            <p className="text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)]">{row.statement}</p>
          </button>
          {open && (
            <>
              <ImpactBlock a={row} shadowLoading={shadowLoading} />
              {onStatusChange && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(['unvalidated', 'confirmed', 'broken'] as const).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => onStatusChange(row.id, s)}
                      className={`text-xs px-2 py-1 rounded border ${
                        row.status === s
                          ? 'border-primary-400 bg-primary-50 text-primary-800 dark:bg-primary-950/40'
                          : 'border-[var(--rule)] dark:border-[var(--rule)] text-[var(--text-sec)]'
                      }`}
                    >
                      Mark {s}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </li>
  );
}

export function ScenarioAssumptionsPanel({
  snapshot,
  loading,
  baselineInput,
  baselineOutput,
  onStatusChange,
}: {
  snapshot: TimePhasedAssumptionsSnapshot | null;
  loading?: boolean;
  baselineInput?: TimePhasedEngineInput | null;
  baselineOutput?: TimePhasedEngineOutput | null;
  onStatusChange?: (id: string, status: TimePhasedAssumptionStatus) => void;
}) {
  const [query, setQuery] = useState('');
  const assumptions = useMemo(() => snapshot?.assumptions ?? [], [snapshot?.assumptions]);
  const countState = getItemCountState(assumptions.length);
  const runKey = snapshot?.runKey ?? (snapshot?.ranAt ? `run:${snapshot.ranAt}` : undefined);

  const sorted = useMemo(() => {
    return [...assumptions].sort((a, b) => {
      const mag = impactMagnitude(b.impact) - impactMagnitude(a.impact);
      if (mag !== 0) return mag;
      const rank = { broken: 0, unvalidated: 1, confirmed: 2 } as const;
      return (rank[a.status] ?? 9) - (rank[b.status] ?? 9);
    });
  }, [assumptions]);

  const filtered = useMemo(() => {
    if (countState !== 'many' || !query.trim()) return sorted;
    const q = query.trim().toLowerCase();
    return sorted.filter(
      (a) =>
        a.statement.toLowerCase().includes(q) ||
        a.subject.toLowerCase().includes(q) ||
        KIND_LABEL[a.kind].toLowerCase().includes(q),
    );
  }, [sorted, query, countState]);

  const atRisk = assumptions.filter((a) => a.status !== 'confirmed').length;

  if (loading) {
    return (
      <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-elevated)] p-4 text-sm text-[var(--text-ter)]">
        Loading assumptions…
      </div>
    );
  }

  if (countState === 'zero') {
    return (
      <div className="border border-dashed border-[var(--rule)] dark:border-[var(--rule)] p-4 flex items-start gap-3">
        <Lightbulb className="h-5 w-5 text-[var(--text-ter)] shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
            What must be true for this plan
          </p>
          <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] mt-1">
            Run placement to emit concrete assumptions — e.g. which team allocates hours which week —
            then expand a row for shadow impact on launches, overload, and funding.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-elevated)] overflow-hidden">
      <div className="px-4 py-3 border-b border-[var(--rule)] dark:border-[var(--rule)]">
        <div className="flex items-center gap-2">
          <Lightbulb className="h-4 w-4 text-primary-600" />
          <h2 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
            What must be true for this plan
          </h2>
        </div>
        <p className="text-xs text-[var(--text-ter)] mt-0.5">
          {assumptions.length} assumption{assumptions.length === 1 ? '' : 's'}
          {atRisk > 0 ? ` · sorted by impact` : ''}
          {snapshot?.ranAt
            ? ` · from ${new Date(snapshot.ranAt).toLocaleString()}`
            : ''}
          {' · expand for shadow impact · status is human-set'}
        </p>
      </div>
      {countState === 'many' && (
        <div className="px-3 pt-3">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search assumptions…"
            className="w-full text-sm border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)] px-3 py-2"
          />
        </div>
      )}
      <ul className="max-h-96 overflow-y-auto">
        {filtered.map((a) => (
          <AssumptionRow
            key={a.id}
            a={a}
            runKey={runKey}
            baselineInput={baselineInput}
            baselineOutput={baselineOutput}
            onStatusChange={onStatusChange}
          />
        ))}
      </ul>
    </div>
  );
}
